<template>
  <div class="contact-tree-node">
    <div
      class="contact-tree-row d-flex align-center"
      :class="{ 'context-only': node.contextOnly }"
      :style="{ paddingLeft: depth * 24 + 'px' }"
    >
      <!-- Expand / collapse toggle (only when there are children) -->
      <v-btn
        v-if="hasChildren"
        :icon="open ? 'mdi-chevron-down' : 'mdi-chevron-right'"
        variant="text"
        size="small"
        density="comfortable"
        class="tree-toggle"
        :title="open ? 'Inklappen' : 'Uitklappen'"
        @click.stop="open = !open"
      />
      <!-- Spacer to keep leaf rows aligned with expandable ones -->
      <div v-else class="tree-toggle-spacer" />

      <ContactListItem :contact="node.contact" class="flex-grow-1">
        <template #after-name>
          <v-chip
            v-if="childSummary"
            size="x-small"
            variant="tonal"
            color="grey"
            class="ml-2 tree-child-count"
            @click.stop="open = !open"
          >
            <v-icon start size="12">mdi-file-tree</v-icon>
            {{ childSummary }}
          </v-chip>
        </template>
      </ContactListItem>
    </div>

    <!-- Children -->
    <v-expand-transition>
      <div v-if="open && hasChildren" class="contact-tree-children">
        <ContactTreeNode
          v-for="child in node.children"
          :key="`node-${child.contact.id}`"
          :node="child"
          :depth="depth + 1"
          :expand-directive="expandDirective"
        />
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import ContactListItem from "./ContactListItem.vue";

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  depth: {
    type: Number,
    default: 0,
  },
  // Global expand control:
  //   null  -> node manages its own open state (user-controlled)
  //   true  -> force open (e.g. a search/filter is active, or "expand all")
  //   false -> force collapsed ("collapse all")
  expandDirective: {
    type: [Boolean, null],
    default: null,
  },
});

const hasChildren = computed(() => props.node.children.length > 0);

// Top-level nodes start open in browse mode so the hierarchy is visible at a
// glance; deeper nodes start collapsed to keep long lists scannable.
const initialOpen =
  props.expandDirective !== null ? props.expandDirective : props.depth === 0;
const open = ref(initialOpen);

// Pass the directive down to children unchanged.
const expandDirective = computed(() => props.expandDirective);

watch(
  () => props.expandDirective,
  (val) => {
    if (val === true) open.value = true;
    else if (val === false) open.value = false;
    // null: leave the user's manual state untouched
  },
);

// "2 afdelingen · 5 personen" style summary of direct children.
const childSummary = computed(() => {
  if (!hasChildren.value) return "";
  let departments = 0;
  let people = 0;
  for (const child of props.node.children) {
    if (child.contact.type === "department") departments += 1;
    else if (child.contact.type === "individual") people += 1;
  }
  const parts = [];
  if (departments > 0) {
    parts.push(`${departments} afdeling${departments !== 1 ? "en" : ""}`);
  }
  if (people > 0) {
    parts.push(`${people} perso${people !== 1 ? "nen" : "on"}`);
  }
  return parts.join(" · ");
});
</script>

<style scoped>
.contact-tree-row {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  transition: background-color 0.2s ease;
}

.contact-tree-row.context-only {
  opacity: 0.55;
}

.tree-toggle {
  flex-shrink: 0;
  margin-left: 4px;
}

.tree-toggle-spacer {
  width: 40px;
  flex-shrink: 0;
}

.tree-child-count {
  cursor: pointer;
}

/* Subtle guide rail for nested levels */
.contact-tree-children {
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
  margin-left: 20px;
}
</style>
